// common utility fns
export const unique = a => [...new Set(a)];

export const deepClone = arr => JSON.parse(JSON.stringify(arr));

export const compareNumber = (a, b) => a - b;

export const range = (start, stop, step = 1) => {
  if (!stop) {
    return [start];
  }
  return Array.from(
    { length: (stop - start) / step + 1 },
    (_, i) => start + i * step
  ).sort(compareNumber);
};

export const last = arr => arr.slice(-1)[0];

export const isSequential = array =>
  array
    .sort(compareNumber)
    .every((num, i) => i === 0 || num - 1 === array[i - 1]);

export const isPlainObj = value =>
  !!value && Object.getPrototypeOf(value) === Object.prototype;

export const validate = {
  // private abstractions
  cell: cell =>
    isPlainObj(cell) &&
    Object.keys(cell).toString() === "coordinates,id" &&
    Array.isArray(cell.coordinates) &&
    !cell.coordinates.some(coordinate => typeof coordinate !== "number") &&
    typeof cell.id === "string",
  rowIndex: rowIndex => typeof rowIndex === "number",
  columnIndex: columnIndex => typeof columnIndex === "number",
  rowIndexes: rowIndexes =>
    Array.isArray(rowIndexes) &&
    rowIndexes.every(validate.rowIndex) &&
    isSequential(rowIndexes), // if more than 1, must be sequential
  columnIndexes: columnIndexes =>
    Array.isArray(columnIndexes) &&
    columnIndexes.every(validate.columnIndex) &&
    isSequential(columnIndexes), // if more than 1, must be sequential
  constructor: layout => {
    if (
      !Array.isArray(layout) ||
      !Array.isArray(layout[0]) ||
      layout.some(row => row.some(arg => !validate.cell(arg)))
    ) {
      throw new Error("An array of cells within an array of rows is required");
    }
  },
  // cells
  mergeCells: (rowIndexes, columnIndexes) => {
    const args = [rowIndexes, columnIndexes];
    if (
      args.some(arg => !Array.isArray(arg)) ||
      args.length !== 2 || // need both rowIndexes and columnIndexes arguments
      args.some(arg => arg.length === 0) || // need at least 1 row and column
      (args[0].length < 2 && args[1].length < 2) || // Either row or column must be more than 1
      !validate.rowIndexes(rowIndexes) ||
      !validate.columnIndexes(columnIndexes)
    ) {
      throw new Error(
        "rowIndexes and columnIndexes arguments must be array of sequential numbers"
      );
    }
  },
  unmergeCells: cellIds => {
    if (
      !Array.isArray(cellIds) ||
      cellIds.length < 1 ||
      cellIds.some(cellId => typeof cellId !== "string")
    ) {
      throw new Error("cellIds argument must be array of UUIDs for a cell");
    }
  },
  // rows
  moveRows: (srcIndexes, destIndex) => {
    if (
      !validate.rowIndexes(srcIndexes) ||
      srcIndexes.length < 1 || // need at least 1 srcIndexes
      typeof destIndex !== "number"
    ) {
      throw new Error(
        "srcIndexes must be array of numbers and and destIndex must be number"
      );
    }
  },
  duplicateRows: rowIndexes => {
    if (
      !validate.rowIndexes(rowIndexes) ||
      rowIndexes.length < 1 // need at least 1 rowIndexes
    ) {
      throw new Error(
        "rowIndexes argument must be array of sequential numbers"
      );
    }
  },
  addRows: rowIndexes => {
    if (
      !validate.rowIndexes(rowIndexes) ||
      rowIndexes.length < 1 // need at least 1 rowIndexes
    ) {
      throw new Error("rowIndexes must be Array of sequential numbers");
    }
  },
  deleteRows: rowIndexes => {
    if (
      !validate.rowIndexes(rowIndexes) ||
      rowIndexes.length < 1 // need at least 1 rowIndexes
    ) {
      throw new Error("rowIndexes must be Array of sequential numbers");
    }
  },
  selectRows: rowIndexes => {
    if (
      !validate.rowIndexes(rowIndexes) ||
      rowIndexes.length < 1 // need at least 1 rowIndexes
    ) {
      throw new Error("rowIndexes must be Array of sequential numbers");
    }
  },
  // columns
  addColumns: columnIndexes => {
    if (
      !validate.columnIndexes(columnIndexes) ||
      columnIndexes.length < 1 // need at least 1 columnIndexes
    ) {
      throw new Error("columnIndexes must be Array of sequential numbers");
    }
  },
  deleteColumns: columnIndexes => {
    if (
      !validate.columnIndexes(columnIndexes) ||
      columnIndexes.length < 1 // need at least 1 columnIndexes
    ) {
      throw new Error("columnIndexes must be Array of sequential numbers");
    }
  },
  selectColumns: columnIndexes => {
    if (
      !validate.columnIndexes(columnIndexes) ||
      columnIndexes.length < 1 // need at least 1 columnIndexes
    ) {
      throw new Error("columnIndexes must be Array of sequential numbers");
    }
  },
  duplicateColumns: columnIndexes => {
    if (
      !validate.columnIndexes(columnIndexes) ||
      columnIndexes.length < 1 // need at least 1 columnIndexes
    ) {
      throw new Error("columnIndexes must be Array of sequential numbers");
    }
  },
  moveColumns: (srcIndexes, destIndex) => {
    if (
      !validate.rowIndexes(srcIndexes) ||
      srcIndexes.length < 1 || // need at least 1 srcIndexes
      typeof destIndex !== "number"
    ) {
      throw new Error(
        "srcIndexes must be array of numbers and and destIndex must be number"
      );
    }
  },
  // actions
  getActionsForRows: rowIndexes => {
    if (
      !validate.rowIndexes(rowIndexes) ||
      rowIndexes.length < 1 // need at least 1 rowIndexes
    ) {
      throw new Error(
        "rowIndexes argument must be array of sequential numbers"
      );
    }
  },
  getActionsForColumns: columnIndexes => {
    if (
      !validate.columnIndexes(columnIndexes) ||
      columnIndexes.length < 1 // need at least 1 columnIndexes
    ) {
      throw new Error(
        "columnIndexes argument must be array of sequential numbers"
      );
    }
  },
  getActionsForCells: cellIds => {
    if (
      !Array.isArray(cellIds) ||
      cellIds.length < 1 ||
      cellIds.some(cellId => typeof cellId !== "string")
    ) {
      throw new Error("cellIds argument must be array of UUIDs for a cell");
    }
  }
};

// constants
export const MOVE_SRC_DOWN = "MOVE_SRC_DOWN";
export const MOVE_SRC_UP = "MOVE_SRC_UP";
export const MOVE_SRC_LEFT = "MOVE_SRC_LEFT";
export const MOVE_SRC_RIGHT = "MOVE_SRC_RIGHT";
